// src/pages/evenements/mariages/ListeMariages.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Spinner, Modal, Alert, Button, Badge, Table, Pagination, Form, InputGroup } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Header from '../../../layouts/Header';
import Footer from '../../../layouts/Footer';
import { ROLES } from '../../../data/Menu';
import { COLORS } from '../../../constants/colors';

// Fonctions utilitaires
const truncateText = (text, maxLength = 25) => {
  if (!text) return '-';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('fr-FR', options);
};

const formatDateSimple = (dateString) => {
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('fr-FR', options);
};

const getStatusConfig = (status) => {
  if (!status) return { color: COLORS.secondary, label: 'Inconnu', icon: 'ri-question-line' };
  const statusLower = status.toString().toLowerCase().trim();
  if (statusLower.includes('program')) return { color: COLORS.info, label: 'Programmé', icon: 'ri-calendar-line' };
  if (statusLower.includes('termin')) return { color: COLORS.success, label: 'Terminé', icon: 'ri-checkbox-circle-line' };
  if (statusLower.includes('annul')) return { color: COLORS.danger, label: 'Annulé', icon: 'ri-close-circle-line' };
  return { color: COLORS.secondary, label: status, icon: 'ri-question-line' };
};

export default function ListeMariages() {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [egliseFilter, setEgliseFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [eglises, setEglises] = useState([]);
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [mariageToDelete, setMariageToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    programme: 0,
    termine: 0,
    annule: 0
  });

  const [permissions, setPermissions] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
    canExport: false,
    canFilter: false
  });

  // ===== Permissions =====
  const hasPermission = (action) => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      const user = JSON.parse(userStr);
      const userRole = user.role || user.type_utilisateur;
      if (userRole === ROLES.SUPER_ADMIN) return true;

      const permissionsMap = {
        'view_mariages': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DP, ROLES.ACCUEIL, ROLES.NOUVEAU, ROLES.MEMBRE],
        'add_mariage': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DP, ROLES.ACCUEIL],
        'edit_mariage': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DP, ROLES.ACCUEIL],
        'delete_mariage': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR],
        'export_mariages': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DP, ROLES.ACCUEIL],
        'filter_mariages': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DP, ROLES.ACCUEIL, ROLES.NOUVEAU, ROLES.MEMBRE]
      };
      return permissionsMap[action]?.includes(userRole) || false;
    } catch (e) {
      console.error('Erreur permission:', e);
      return false;
    }
  };

  useEffect(() => {
    setPermissions({
      canView: hasPermission('view_mariages'),
      canAdd: hasPermission('add_mariage'),
      canEdit: hasPermission('edit_mariage'),
      canDelete: hasPermission('delete_mariage'),
      canExport: hasPermission('export_mariages'),
      canFilter: hasPermission('filter_mariages')
    });
  }, []);

  // ===== API =====
  const fetchEglises = useCallback(async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return;
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const response = await fetch(`https://josephsbusiness.com/api/eglises.php?user_id=${userId}&type=siege&limit=1000`, {
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      if (result.success) {
        setEglises(result.eglises || []);
      }
    } catch (err) {
      console.error('Erreur chargement églises:', err);
    }
  }, []);

  const fetchMariages = useCallback(async () => {
    if (data) setFilterLoading(true);
    else setLoading(true);

    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/pages/signin2');
        return;
      }
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;

      const timestamp = Date.now();
      let url = `https://josephsbusiness.com/api/mariages.php?user_id=${userId}&page=${currentPage}&limit=${itemsPerPage}&_=${timestamp}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      if (egliseFilter) url += `&eglise=${encodeURIComponent(egliseFilter)}`;
      if (typeFilter) url += `&type=${encodeURIComponent(typeFilter)}`;
      if (statusFilter) url += `&status=${encodeURIComponent(statusFilter)}`;

      const response = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);
      const result = await response.json();

      if (result.success) {
        setData(result);
        if (result.mariages) {
          let programme = 0, termine = 0, annule = 0;
          result.mariages.forEach(mariage => {
            const status = mariage.status?.toString().toLowerCase().trim() || '';
            if (status.includes('program')) programme++;
            else if (status.includes('termin')) termine++;
            else if (status.includes('annul')) annule++;
          });
          setStats({
            total: result.pagination?.total || 0,
            programme,
            termine,
            annule
          });
        }
      } else {
        setError(result.message || 'Erreur de chargement');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setFilterLoading(false);
    }
  }, [currentPage, searchTerm, egliseFilter, typeFilter, statusFilter, itemsPerPage, navigate]);

  useEffect(() => {
    fetchEglises();
  }, [fetchEglises]);

  useEffect(() => {
    fetchMariages();
  }, [fetchMariages, refreshTrigger]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reload = params.get('reload');
    if (reload) {
      setRefreshTrigger(prev => prev + 1);
      navigate('/evenements/mariages', { replace: true });
    }
  }, [location, navigate]);

  // ===== Handlers =====
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setRefreshTrigger(prev => prev + 1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setRefreshTrigger(prev => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteClick = (mariage) => {
    setMariageToDelete(mariage);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!mariageToDelete) return;
    setDeleting(true);
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const response = await fetch(`https://josephsbusiness.com/api/mariages.php?user_id=${userId}&id=${mariageToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      if (result.success) {
        setShowDeleteModal(false);
        setMariageToDelete(null);
        setRefreshTrigger(prev => prev + 1);
        setSuccessMessage('Mariage supprimé avec succès');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      } else {
        alert(result.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const getEgliseName = (matricule) => {
    const eglise = eglises.find(e => e.matricule === matricule);
    return eglise ? eglise.nom : matricule;
  };

  const exportToExcel = () => {
    if (!data?.mariages) return;
    const fileName = `mariages_${new Date().toISOString().split('T')[0]}.xlsx`;
    const exportData = data.mariages.map(item => ({
      'Matricule': item.matricule,
      'Époux': item.person1,
      'Épouse': item.personne2,
      'Date': formatDateSimple(item.date_at),
      'Lieu': item.lieu || '-',
      'Type': item.type,
      'Statut': item.status,
      'Église': getEgliseName(item.matri_eglise)
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.sheet_add_aoa(ws, [
      ['LISTE DES MARIAGES'],
      [`Généré le ${new Date().toLocaleDateString('fr-FR')}`],
      [`Total: ${stats.total} mariages`],
      []
    ], { origin: 'A1' });
    XLSX.utils.book_append_sheet(wb, ws, "Mariages");
    XLSX.writeFile(wb, fileName);
  };

  const printPDF = () => {
    const printWindow = window.open('', '_blank');
    const mariages = data?.mariages || [];
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Liste des mariages</title>
        <meta charset="UTF-8">
        <style>
          body { font-family: Arial; margin: 30px; }
          .header { text-align: center; border-bottom: 2px solid #295AA7; margin-bottom: 30px; }
          h1 { color: #295AA7; }
          .summary { background: #f8fafc; padding: 20px; border-left: 4px solid #f43f5e; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th { background: #295AA7; color: white; padding: 8px; text-align: left; }
          td { padding: 6px 8px; border: 1px solid #ddd; }
          tr:nth-child(even) { background: #f8fafc; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Liste des mariages</h1>
          <p>Généré le ${new Date().toLocaleDateString('fr-FR')}</p>
        </div>
        <div class="summary">
          <p><strong>Total mariages:</strong> ${stats.total}</p>
          <p><strong>Programmés:</strong> ${stats.programme} | <strong>Terminés:</strong> ${stats.termine} | <strong>Annulés:</strong> ${stats.annule}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th>Matricule</th>
              <th>Époux</th>
              <th>Épouse</th>
              <th>Date</th>
              <th>Lieu</th>
              <th>Type</th>
              <th>Statut</th>
              <th>Église</th>
            </tr>
          </thead>
          <tbody>
            ${mariages.map(m => `
              <tr>
                <td>${m.matricule}</td>
                <td>${truncateText(m.person1, 15)}</td>
                <td>${truncateText(m.personne2, 15)}</td>
                <td>${formatDateSimple(m.date_at)}</td>
                <td>${m.lieu ? truncateText(m.lieu, 15) : '-'}</td>
                <td>${m.type}</td>
                <td>${m.status}</td>
                <td>${truncateText(getEgliseName(m.matri_eglise), 15)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  };

  // ===== Loading =====
  if (loading && !data) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
              <h5 className="fw-bold mt-3" style={{ color: COLORS.primaryDark }}>Chargement des mariages</h5>
              <p className="text-muted">Récupération des données en cours...</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error && !data) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <Alert variant="danger" className="border-0 shadow-sm rounded-3">
            <div className="d-flex align-items-center">
              <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
              <div>
                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                <p className="mb-0 small">{error}</p>
                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={() => setRefreshTrigger(prev => prev + 1)}>
                  <i className="ri-refresh-line me-1"></i>Réessayer
                </Button>
              </div>
            </div>
          </Alert>
        </div>
        <Footer />
      </>
    );
  }

  const mariages = data?.mariages || [];
  const pagination = data?.pagination || { total: 0, pages: 1 };

  // Pagination
  const renderPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    const totalPages = pagination.pages;
    if (totalPages <= 1) return items;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)} className="pagination-item">
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis-start" disabled className="pagination-ellipsis" />);
      }
    }
    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item 
          key={number} 
          active={number === currentPage} 
          onClick={() => handlePageChange(number)}
          className="pagination-item"
        >
          {number}
        </Pagination.Item>
      );
    }
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" disabled className="pagination-ellipsis" />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)} className="pagination-item">
          {totalPages}
        </Pagination.Item>
      );
    }
    return items;
  };

  // ===== Rendu principal =====
  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
        <div className="container-fluid px-0 px-lg-3">

          {/* ===== EN‑TÊTE MODERNE (comme AjouterEglise) ===== */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper" style={{ background: `linear-gradient(135deg, ${COLORS.rose}, #be123c)` }}>
                <i className="ri-heart-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Mariages</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-heart-line"></i> Total {stats.total}
                  </span>
                  <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                    <i className="ri-checkbox-circle-line"></i> Terminés {stats.termine}
                  </span>
                  
                  {/* <span className="badge-modern" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                    <i className="ri-close-circle-line"></i> Annulés {stats.annule}
                  </span> */}
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              {permissions.canFilter && (
                <Button size="sm" className="btn-modern-outline" onClick={() => setShowFilters(!showFilters)}>
                  <i className="ri-filter-line"></i> {showFilters ? 'Masquer' : 'Filtres'}
                </Button>
              )}
              {permissions.canExport && (
                <>
                  <Button size="sm" className="btn-modern-outline" onClick={exportToExcel}>
                    <i className="ri-file-excel-line"></i> Excel
                  </Button>
                  <Button size="sm" className="btn-modern-outline" onClick={printPDF}>
                    <i className="ri-file-pdf-line"></i> PDF
                  </Button>
                </>
              )}
              {permissions.canAdd && (
                <Button size="sm" className="btn-modern-primary" onClick={() => navigate('/evenements/mariagesNouveau')}>
                  <i className="ri-add-line"></i> Nouveau
                </Button>
              )}
              <Button size="sm" className="btn-modern-outline" onClick={() => setRefreshTrigger(prev => prev + 1)} disabled={filterLoading}>
                <i className={`ri-refresh-line ${filterLoading ? 'fa-spin' : ''}`}></i> Actualiser
              </Button>
            </div>
          </div>

          {/* ===== MESSAGE DE SUCCÈS ===== */}
          {showSuccessMessage && (
            <Alert
              variant="success"
              className="border-0 shadow-sm rounded-3 mb-3"
              onClose={() => setShowSuccessMessage(false)}
              dismissible
            >
              <div className="d-flex align-items-center">
                <i className="ri-checkbox-circle-line fs-4 me-2" style={{ color: COLORS.success }}></i>
                <span>{successMessage}</span>
              </div>
            </Alert>
          )}

          {/* ===== KPI CARDS (comme Membres) ===== */}
          <Row className="g-3 mb-4">
            <Col sm="6" xl="3">
              <div className="kpi-card kpi-primary">
                <div className="kpi-icon"><i className="ri-heart-line fs-24"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Total</span>
                  <h3 className="kpi-value">{stats.total}</h3>
                </div>
              </div>
            </Col>
            <Col sm="6" xl="3">
              <div className="kpi-card kpi-success">
                <div className="kpi-icon"><i className="ri-checkbox-circle-line fs-24"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Terminés</span>
                  <h3 className="kpi-value">{stats.termine}</h3>
                </div>
              </div>
            </Col>
            <Col sm="6" xl="3">
              <div className="kpi-card kpi-info">
                <div className="kpi-icon"><i className="ri-calendar-line fs-24"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Programmés</span>
                  <h3 className="kpi-value">{stats.programme}</h3>
                </div>
              </div>
            </Col>
            <Col sm="6" xl="3">
              <div className="kpi-card kpi-danger">
                <div className="kpi-icon"><i className="ri-close-circle-line fs-24"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Annulés</span>
                  <h3 className="kpi-value">{stats.annule}</h3>
                </div>
              </div>
            </Col>
          </Row>

          {/* ===== FILTRES (fond primaryDark, comme Membres) ===== */}
          {permissions.canFilter && showFilters && (
            <Card className="border-0 shadow-sm rounded-3 mb-4" style={{ background: COLORS.primaryDark }}>
              <Card.Body className="py-3 px-4">
                <Form onSubmit={handleSearch}>
                  <Row className="align-items-center g-3">
                    <Col md="3">
                      <div className="position-relative">
                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-white-50 fs-14"></i>
                        <Form.Control
                          type="text"
                          placeholder="Époux, épouse..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="ps-5 rounded-pill"
                          style={{ background: 'rgba(255,255,255,0.9)' }}
                        />
                      </div>
                    </Col>
                    <Col md="2">
                      <Form.Select
                        value={egliseFilter}
                        onChange={(e) => setEgliseFilter(e.target.value)}
                        className="rounded-pill px-3"
                        style={{ background: 'rgba(255,255,255,0.9)' }}
                      >
                        <option value="">Église</option>
                        {eglises.map(eglise => (
                          <option key={eglise.matricule} value={eglise.matricule}>
                            {truncateText(eglise.nom, 15)}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md="2">
                      <Form.Select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="rounded-pill px-3"
                        style={{ background: 'rgba(255,255,255,0.9)' }}
                      >
                        <option value="">Type</option>
                        <option value="Religieux">Religieux</option>
                        <option value="Civil">Civil</option>
                        <option value="Traditionnel">Traditionnel</option>
                      </Form.Select>
                    </Col>
                    <Col md="2">
                      <Form.Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="rounded-pill px-3"
                        style={{ background: 'rgba(255,255,255,0.9)' }}
                      >
                        <option value="">Statut</option>
                        <option value="Programmé">Programmé</option>
                        <option value="Terminé">Terminé</option>
                        <option value="Annulé">Annulé</option>
                      </Form.Select>
                    </Col>
                    <Col md="3">
                      <Button
                        type="submit"
                        variant="light"
                        size="sm"
                        className="rounded-pill w-100"
                        disabled={filterLoading}
                      >
                        {filterLoading ? <Spinner size="sm" /> : <i className="ri-check-line"></i>}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          )}

          {/* ===== TABLEAU (en‑tête secondary) ===== */}
          <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
            <Card.Header className="border-0 py-3" style={{ background: COLORS.secondary }}>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <i className="ri-list-check text-white me-2 fs-5"></i>
                  <span className="fw-semibold text-white">Liste des mariages</span>
                  <Badge bg="light" text="dark" className="ms-2 rounded-pill">
                    {pagination.total} au total
                  </Badge>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Form.Select
                    size="sm"
                    style={{ width: 'auto', background: 'rgba(255,255,255,0.9)' }}
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                  </Form.Select>
                  {filterLoading && <Spinner animation="border" size="sm" variant="light" />}
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th className="ps-4">Matricule</th>
                      <th>Époux</th>
                      <th>Épouse</th>
                      <th>Date</th>
                      <th>Lieu</th>
                      <th>Type</th>
                      <th>Statut</th>
                      <th>Église</th>
                      <th className="text-center pe-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mariages.length > 0 ? (
                      mariages.map((mariage) => {
                        const statusConfig = getStatusConfig(mariage.status);
                        return (
                          <tr key={mariage.id}>
                            <td className="ps-4">
                              <span className="reference-badge">{mariage.matricule}</span>
                            </td>
                            <td>{truncateText(mariage.person1, 15)}</td>
                            <td>{truncateText(mariage.personne2, 15)}</td>
                            <td>{formatDateSimple(mariage.date_at)}</td>
                            <td>{mariage.lieu ? truncateText(mariage.lieu, 12) : '-'}</td>
                            <td>{mariage.type}</td>
                            <td>
                              <span
                                className="badge rounded-pill"
                                style={{
                                  backgroundColor: statusConfig.color,
                                  color: 'white',
                                  padding: '0.25rem 0.6rem',
                                  fontSize: '0.7rem'
                                }}
                              >
                                <i className={statusConfig.icon + ' me-1'}></i>
                                {statusConfig.label}
                              </span>
                            </td>
                            <td>{truncateText(getEgliseName(mariage.matri_eglise), 12)}</td>
                            <td className="text-center pe-4">
                              <div className="d-flex gap-2 justify-content-center">
                                {permissions.canEdit && (
                                  <button
                                    className="action-icon action-icon-primary"
                                    onClick={() => navigate(`/evenements/mariages/modifier/${mariage.id}`)}
                                    title="Modifier"
                                  >
                                    <i className="ri-pencil-line"></i>
                                  </button>
                                )}
                                {permissions.canDelete && (
                                  <button
                                    className="action-icon action-icon-danger"
                                    onClick={() => handleDeleteClick(mariage)}
                                    title="Supprimer"
                                  >
                                    <i className="ri-delete-bin-line"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center py-5">
                          <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                          <h6 className="mb-1">Aucun mariage trouvé</h6>
                          <p className="text-muted small mb-0">Essayez de modifier vos critères de recherche</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          {/* ===== PAGINATION ===== */}
          {pagination.pages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4 px-3">
              <div className="text-muted small">
                Page {currentPage} sur {pagination.pages}
              </div>
              <Pagination className="mb-0">
                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                {renderPaginationItems()}
                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.pages} />
                <Pagination.Last onClick={() => handlePageChange(pagination.pages)} disabled={currentPage === pagination.pages} />
              </Pagination>
            </div>
          )}

          <Footer />
        </div>
      </div>

 {/* ===== MODAL DE SUPPRESSION (simplifié) ===== */}
<Modal
  show={showDeleteModal}
  onHide={() => setShowDeleteModal(false)}
  centered
  size="md"
  className="delete-modal-modern"
>
  <Modal.Header
    closeButton
    className="border-0 pb-0"
    style={{
      background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`,
      borderTopLeftRadius: '4px',
      borderTopRightRadius: '4px'
    }}
  >
    <Modal.Title className="w-100 text-center">
      <div className="delete-icon-wrapper mb-2">
        <div className="delete-icon-circle" style={{
          width: '70px',
          height: '70px',
          borderRadius: '4px',
          background: 'rgba(255,255,255,0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto'
        }}>
          <i className="ri-heart-2-fill" style={{ fontSize: '32px', color: 'white' }}></i>
        </div>
      </div>
      <h5 className="fw-bold text-white mb-0">Confirmer la suppression</h5>
      <p className="text-white-50 small mb-0">Cette action est irréversible</p>
    </Modal.Title>
  </Modal.Header>

  <Modal.Body className="pt-4 px-4">
    {mariageToDelete && (
      <>
        <p className="text-center text-muted mb-3">
          Êtes-vous sûr de vouloir supprimer définitivement ce mariage ?
        </p>
        <div className="p-3 mb-3" style={{
          background: '#f8fafc',
          borderLeft: `3px solid ${COLORS.primary}`,
          borderRadius: '4px'
        }}>
          <div className="fw-bold" style={{ color: COLORS.primaryDark }}>
            {mariageToDelete.person1} & {mariageToDelete.personne2}
          </div>
          <div className="text-muted small">
            <i className="ri-calendar-line me-1"></i>
            {formatDate(mariageToDelete.date_at)}
          </div>
          <span className="badge rounded-pill mt-1" style={{
            backgroundColor: getStatusConfig(mariageToDelete.status).color,
            color: 'white'
          }}>
            {getStatusConfig(mariageToDelete.status).label}
          </span>
        </div>
        <div style={{
          background: `${COLORS.danger}10`,
          border: `1px solid ${COLORS.danger}30`,
          borderRadius: '4px',
          padding: '0.8rem 1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.8rem'
        }}>
          <i className="ri-alert-line" style={{ color: COLORS.danger, fontSize: '1.3rem' }}></i>
          <span className="small text-muted">
            Toutes les données associées à ce mariage seront définitivement supprimées.
          </span>
        </div>
      </>
    )}
  </Modal.Body>

  <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-center gap-2">
    <Button
      variant="light"
      onClick={() => setShowDeleteModal(false)}
      disabled={deleting}
      className="px-4"
      style={{
        borderRadius: '4px',
        padding: '0.5rem 1.5rem',
        border: '1px solid #d1d5db',
        color: '#4b5563',
        fontWeight: '500',
        fontSize: '0.85rem',
        transition: 'all 0.2s',
        background: 'white'
      }}
    >
      Annuler
    </Button>
    <Button
      variant="danger"
      onClick={handleDeleteConfirm}
      disabled={deleting}
      className="px-4"
      style={{
        background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`,
        border: 'none',
        borderRadius: '4px',
        padding: '0.5rem 1.5rem',
        fontWeight: '500',
        fontSize: '0.85rem',
        boxShadow: '0 4px 10px rgba(239, 68, 68, 0.25)',
        transition: 'all 0.2s',
        color: 'white'
      }}
      onMouseEnter={(e) => {
        e.target.style.transform = 'translateY(-2px)';
        e.target.style.boxShadow = '0 8px 16px rgba(239, 68, 68, 0.35)';
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = 'translateY(0)';
        e.target.style.boxShadow = '0 4px 10px rgba(239, 68, 68, 0.25)';
      }}
    >
      {deleting ? (
        <><Spinner size="sm" className="me-1" /> Suppression...</>
      ) : (
        <><i className="ri-delete-bin-line me-2"></i>Oui, supprimer</>
      )}
    </Button>
  </Modal.Footer>
</Modal>
      {/* ===== STYLES (comme AjouterEglise) ===== */}
      <style>{`
        .fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .header-modern {
          background: white;
          padding: 1.2rem 1.8rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          border-left: 4px solid ${COLORS.secondary};
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
        }
        .header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .header-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.4rem;
        }
        .gradient-text {
          color: #1e293b;
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0;
        }
        .badge-modern {
          background: #f1f4f9;
          color: #475569;
          padding: 0.3rem 0.8rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .badge-modern i {
          color: ${COLORS.secondary};
        }
        .btn-modern-outline {
          border: 1px solid #d1d5db;
          background: white;
          padding: 0.4rem 1.2rem;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 500;
          color: #4b5563;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .btn-modern-outline:hover {
          border-color: ${COLORS.secondary};
          color: ${COLORS.secondary};
          background: #fafafa;
        }
        .btn-modern-primary {
          background: ${COLORS.primary};
          border: none;
          padding: 0.4rem 1.2rem;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 500;
          color: white;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s;
        }
        .btn-modern-primary:hover {
          background: ${COLORS.primaryDark};
          box-shadow: 0 4px 10px rgba(41,90,167,0.3);
        }

        /* KPI */
        .kpi-card {
          background: white;
          border-radius: 0px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.2s ease;
          height: 100%;
          border-left: 2px solid;
        }
        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .kpi-primary { border-left-color: ${COLORS.primary}; }
        .kpi-success { border-left-color: ${COLORS.success}; }
        .kpi-danger { border-left-color: ${COLORS.danger}; }
        .kpi-warning { border-left-color: ${COLORS.warning}; }
        .kpi-info { border-left-color: ${COLORS.info}; }

        .kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .kpi-primary .kpi-icon { background: rgba(41, 90, 167, 0.1); color: ${COLORS.primary}; }
        .kpi-success .kpi-icon { background: rgba(16, 185, 129, 0.1); color: ${COLORS.success}; }
        .kpi-danger .kpi-icon { background: rgba(239, 68, 68, 0.1); color: ${COLORS.danger}; }
        .kpi-warning .kpi-icon { background: rgba(245, 158, 11, 0.1); color: ${COLORS.warning}; }
        .kpi-info .kpi-icon { background: rgba(59, 130, 246, 0.1); color: ${COLORS.info}; }

        .kpi-content { flex: 1; }
        .kpi-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          color: #6c757d;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .kpi-value {
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0.25rem 0;
          color: #1a3c5e;
        }

        /* Actions */
        .action-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          color: #6c757d;
          transition: all 0.2s;
          cursor: pointer;
        }
        .action-icon:hover {
          background: ${COLORS.primary};
          border-color: ${COLORS.primary};
          color: white !important;
        }
        .action-icon-primary:hover {
          background: ${COLORS.primary};
          border-color: ${COLORS.primary};
          color: white !important;
        }
        .action-icon-danger:hover {
          background: #dc3545;
          border-color: #dc3545;
          color: white !important;
        }

        .reference-badge {
          background: #f8f9fa;
          padding: 0.2rem 0.6rem;
          border-radius: 6px;
          font-size: 0.7rem;
          font-family: monospace;
        }

        .fs-24 { font-size: 1.5rem; }
        .fs-48 { font-size: 3rem; }
        .fs-14 { font-size: 0.875rem; }
        .rounded-3 { border-radius: 0px !important; }
        .rounded-pill { border-radius: 50px !important; }

        /* Modal */
        .delete-modal-elegant .modal-content {
          border: none;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0,0,0,0.15);
          animation: slideUp 0.3s ease-out;
        }
        .delete-modal-elegant .modal-header {
          background: linear-gradient(135deg, #ffffff, #f8fafc);
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulseIcon {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.9; }
        }
        .delete-icon-wrapper { animation: pulseIcon 2s infinite; }
        .mariage-card { transition: all 0.2s ease; }
        .mariage-card:hover { transform: translateY(-2px); box-shadow: 0 10px 20px rgba(0,0,0,0.05); }
        .info-item { transition: all 0.2s ease; }
        .info-item:hover { background: #ffffff !important; box-shadow: 0 4px 8px rgba(0,0,0,0.02); }
        .epoux-item { transition: all 0.2s ease; }
        .epoux-item:hover { background: #f8fafc !important; box-shadow: 0 4px 8px rgba(0,0,0,0.02); }
        .warning-message { transition: all 0.2s ease; }
        .warning-message:hover { background: ${COLORS.danger}10 !important; }
        .modal-footer button { transition: all 0.3s ease; }
        .modal-footer button:hover { transform: translateY(-2px); box-shadow: 0 10px 20px rgba(0,0,0,0.1); }
        .modal-footer button:active { transform: translateY(0); }

        /* Table */
        .table thead th {
          background: #f8fafc;
          color: #475569;
          font-weight: 600;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
        }
        .table td {
          white-space: nowrap;
          max-width: 120px;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .table tbody tr:hover {
          background: #f8fafc !important;
        }

        @media (max-width: 768px) {
          .header-modern {
            flex-direction: column;
            align-items: stretch;
            gap: 0.8rem;
            padding: 1rem;
          }
          .header-left {
            flex-wrap: wrap;
          }
          .table td {
            max-width: 80px;
          }
        }
      `}</style>
    </>
  );
}